import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import appConfig from '@/utils/appConfig'
import { delay } from '@/utils/delay'
import { shouldShowPromoModal } from '@/services/promo-campaign'
import PaymentSuccessModal from '@/pages/payment/modals/PaymentSuccessModal.vue'

Vue.use(VueRouter)

// SEO: Close versioned pages from indexing and add dynamic canonical link
const termsVersionedRoutes = store.getters['termsOfUse/getTermsVersions'].map(version => ({
  path: `/terms-of-use-${version}`,
  name: `terms-of-use-${version}`,
  component: () =>
      import(/* webpackChunkName: "terms-of-use" */ `../pages/terms-of-use/${version}.vue`),
  meta: {
    requiresAuth: false,
    title: `Terms of Use – ${version}`,
    showReportAbuseBanner: true,
    noindex: true, // SEO requirement
    dynamicCanonical: true // Tells the manager to create a link to the current URL
  },
}))

const termsRedirectRoute = {
  path: '/terms-of-use',
  redirect: `/terms-of-use/${store.getters['termsOfUse/getCurrentTermsVersion']}`,
}

const routes = [
  {
    path: '/en',
    name: 'en',
    redirect: '/'
  },
  {
    path: '/',
    name: 'landing-page',
    component: () =>
        import(/* webpackChunkName: "landing-page" */ '../pages/landing-page'),
    meta: {
      requiresAuth: false,
      title: 'Free Crypto Wallet Risk Checker | Satoshi Eye',
      description: 'Scan any crypto address for blacklist, scams, or darknet exposure. Get instant AML risk reports. No signup. Mobile-ready.',
      ogTitle: 'Instant Crypto Wallet Risk Checker — Satoshi Eye',
      ogDescription: 'Free AML checker for any wallet. Flag detection. No sign-up.',
      canonical: 'https://satoshieye.com/',
      showReportAbuseBanner: false,
      // REQ-2: Schema markup only for the home page
      jsonLD: [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://satoshieye.com/#website",
          "name": "Satoshi Eye",
          "url": "https://satoshieye.com"
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Satoshi Eye",
          "url": "https://satoshieye.com",
          "logo": "https://satoshieye.com/icons/icon-192.png",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Pricing Packages",
            "itemListElement": [
              { "@type": "Offer", "name": "Lite", "price": "5", "priceCurrency": "EUR" },
              { "@type": "Offer", "name": "Plus", "price": "24.99", "priceCurrency": "EUR" },
              { "@type": "Offer", "name": "Standard", "price": "99.99", "priceCurrency": "EUR" },
              { "@type": "Offer", "name": "Advanced", "price": "244.99", "priceCurrency": "EUR" }
            ]
          }
        }
      ]
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../pages/login'),
    meta: {
      title: 'Login',
      noindex: true, // SEO requirement
    },
  },
  {
    path: '/access',
    name: 'access',
    component: () => import(/* webpackChunkName: "b2b-login" */ '../pages/b2b-login'),
    meta: {
      title: 'access',
      noindex: true,
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () =>
        import(/* webpackChunkName: "register" */ '../pages/register'),
    meta: {
      title: 'Register',
      noindex: true,
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () =>
        import(/* webpackChunkName: "reset-password" */ '../pages/register'),
    meta: {
      title: 'Reset password',
      noindex: true,
    },
  },
  {
    path: '/confirm',
    name: 'confirm',
    component: () =>
        import(/* webpackChunkName: "confirm" */ '../pages/register'),
    meta: {
      title: 'Confirm',
      noindex: true,
    },
  },
  {
    path: '/report',
    name: 'report',
    component: () => import(/* webpackChunkName: "report" */ '../pages/report'),
    meta: {
      requiresAuth: false,
      title: 'Report',
      showReportAbuseBanner: true,
      noindex: true, // Excluded from index per SEO requirements
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () =>
        import(/* webpackChunkName: "privacy-policy" */ '../pages/privacy-policy'),
    meta: {
      requiresAuth: false,
      title: 'Privacy policy',
      showReportAbuseBanner: true,
      noindex: true,
    },
  },
  {
    path: '/cookie-policy',
    name: 'cookie-policy',
    component: () =>
        import(/* webpackChunkName: "cookie-policy" */ '../pages/cookie-policy'),
    meta: {
      requiresAuth: false,
      title: 'Cookie Policy',
      showReportAbuseBanner: true,
      noindex: true,
    },
  },
  {
    path: '/enter-code',
    name: 'enter-code',
    component: () => import(/* webpackChunkName: "enter-code" */ '../pages/login'),
    meta: {
      requiresAuth: false,
      title: 'Enter code',
      showReportAbuseBanner: false,
      noindex: true,
    },
  },
  {
    path: '/user-agreement',
    name: 'user-agreement',
    component: () => import(/* webpackChunkName: "user-agreement" */ '../pages/login'),
    meta: {
      requiresAuth: true,
      title: 'User Agreement',
      showReportAbuseBanner: false,
      noindex: true,
    },
  },
  {
    path: '/coinify-callback',
    name: 'coinify-callback',
    component: () => import(/* webpackChunkName: "coinify-callback" */ '../pages/payment/3rd-party/coinify/CoinifyCallbackPage'),
    meta: {
      title: 'Coinify Callback',
      noindex: true,
    },
  },
  // Other internal pages (Profile, Admin, etc.) are closed from indexing by default via the global manager
  {
    path: '/source-of-funds',
    name: 'source-of-funds',
    component: () => import('../pages/source-of-funds'),
    meta: { requiresAuth: true, title: 'Reports', noindex: true },
  },
  {
    path: '/your-reports',
    name: 'your-reports',
    component: () => import('../pages/your-reports'),
    meta: { requiresAuth: true, title: 'Your Reports', noindex: true },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../pages/profile'),
    meta: { requiresAuth: true, title: 'My Account', noindex: true },
  },
  {
    path: '/profile/change-plan',
    name: 'changePlan',
    component: () => import('../pages/profile/sub-pages/ChangePlanPage.vue'),
    meta: { requiresAuth: true, title: 'Change Plan', noindex: true },
  },
  {
    path: '/admin-panel',
    name: 'admin-panel',
    component: () => import('../pages/admin-panel'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin panel', noindex: true },
    redirect: '/admin-panel/users',
    children: [
      { path: 'users', name: 'admin-panel-users', component: () => import('../pages/admin-panel/users'), meta: { noindex: true } },
      { path: 'promo', name: 'admin-panel-promo', component: () => import('../pages/admin-panel/promo'), meta: { noindex: true } },
    ],
  },
  ...termsVersionedRoutes,
  termsRedirectRoute,
  {
    path: '/report-abuse',
    name: 'report-abuse',
    component: () => import('../pages/report-abuse'),
    meta: { requiresAuth: false, title: 'Report Abuse', noindex: true },
  },
  {
    path: '/403',
    name: '403',
    component: () => import('../pages/403'),
    meta: { requiresAuth: false, title: 'Access Denied', noindex: true },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../pages/404'),
    meta: { requiresAuth: false, title: 'Not Found', noindex: true }
  },
  {
    path: '*',
    redirect: '/404'
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

// Preserve all existing beforeEach logic without changes
router.beforeEach(async (to, from, next) => {
  if (window.__PRERENDER_INJECTED) return next();

  store.commit('adminPanel/SWITCH_FILTER_CLEAR_TRIGGER')
  store.commit('report/SET_ACTIVE_ROW_NAME', null)

  const allowedWithoutAgreement = [
    '403',
    'user-agreement',
    'privacy-policy',
    'cookie-policy',
    ...store.getters['termsOfUse/getTermsVersions'].map(v => `terms-of-use-${v}`),
  ]

  const userRole = store.getters['user/userRole']

  if (to.query.session_id && to.query.payment_success) {
    const sessionId = to.query.session_id
    if (sessionId) {
      const { data } = await store.dispatch("payment/syncCheckout", { sessionId });
      if (!data.synced) {
        await delay(2000);
        await store.dispatch("payment/syncCheckout", { sessionId });
      }
    }
    const q = to.query.payment_success
    store.dispatch('modal/openModal', {
      component: PaymentSuccessModal,
      props: {
        isRefreshAction: q === 'refresh',
        onContinue: async () => {
          await store.dispatch('payment/fetchCurrentPlan')
          const comeBackPath = localStorage.getItem('comeBackPath')
          if (comeBackPath) {
            localStorage.removeItem('comeBackPath')
            try { await router.push(comeBackPath) } catch (error) { console.warn('Navigation error:', error) }
          }
        },
      },
    })
    const rest = Object.fromEntries(
        Object.entries(to.query).filter(([key]) => !['payment_success', 'session_id', 'billingCycle', 'planTier', 'isTrialRequest', 'actionStatus'].includes(key))
    )
    router.replace({ path: to.path, query: rest })
  } else if (to.query.payment_success) {
    const rest = Object.fromEntries(
        Object.entries(to.query).filter(([key]) => !['payment_success', 'session_id'].includes(key))
    )
    router.replace({ path: to.path, query: rest })
  }

  if (
    store.getters['user/isLogin'] &&
    store.getters['user/isUserAgree'] &&
    store.getters['user/isAgeConfirmed'] &&
    !store.getters['payment/getCurrentPlan']
  ) {
    await store.dispatch('payment/fetchCurrentPlan')
  }

  if (to.query.access_token && to.name !== 'access') {
    const cleanRoute = { path: to.path, query: { ...to.query }}
    delete cleanRoute.query.access_token
    localStorage.setItem('to.fullPath', router.resolve(cleanRoute).href)
    next({ name: 'access', query: { access_token: to.query.access_token } })
    return
  }

  if ((to.name === 'payment' || to.name === 'changePlan') && store.getters['user/isB2bUser']) {
    next({ name: 'source-of-funds' })
    return
  }

  if (to.name === 'payment' && to.query.promo) {
    localStorage.setItem('promocode', to.query.promo)
  }

  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  if (requiresAdmin && !store.getters['user/isAdmin']) {
    next({ name: 'source-of-funds' })
    return
  }

  if (to.name === 'login' && userRole) {
    next({ name: 'source-of-funds' })
    return
  }

  if (to.name === 'source-of-funds' && from.name != 'login' && !userRole) {
    next({ name: 'login' })
    return
  }

  if (to.meta.requiresFeatureToggle) {
    const featureToggleValue = appConfig[`VUE_APP_${to.meta.requiresFeatureToggle}`]
    if (!featureToggleValue) {
      next({ name: 'source-of-funds' })
      return
    }
  }

  if (to.name === 'register') {
    await store.dispatch('user/logout')
    next()
    return
  }

  if (to.name === 'report') {
    localStorage.setItem('to.fullPath', to.fullPath)
  }

  if (
    store.getters['user/isLogin'] &&
    store.getters['user/isUserAgree'] &&
    store.getters['user/isAgeConfirmed']
  ) {
    const promoResult = await store.dispatch('payment/fetchPromoCampaignIfNeeded')
    if (promoResult?.success && promoResult?.data && shouldShowPromoModal({ ...promoResult.data })) {
      if (store.getters['user/isLogin']) {
        await store.dispatch('payment/checkAndShowPromoModal')
      }
    }
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (localStorage.getItem('cookie:accepted')) {
      localStorage.setItem('to.fullPath', to.fullPath)
    }
    const token = await store.dispatch('user/initToken')
    if (!token) {
      next({ name: 'login' })
      return
    }
    const isRejected = await store.dispatch('user/getMe')
    if (isRejected) return
    if (
      store.getters['user/isLogin'] &&
      (!store.getters['user/isUserAgree'] || !store.getters['user/isAgeConfirmed'])
    ) {
      if (!allowedWithoutAgreement.includes(to.name)) {
        next({ name: 'user-agreement' })
        return
      }
    }
  }
  next()
})

// Updated global SEO manager in afterEach
router.afterEach((to) => {
  Vue.nextTick(() => {
    // 1. Title
    document.title = to.meta.title || 'Satoshi Eye'

    // 2. Meta Description
    const description = to.meta.description || 'Scan any crypto address for blacklist, scams, or darknet exposure. Get instant AML risk reports. No signup. Mobile-ready.'
    let descTag = document.querySelector('meta[name="description"]')
    if (!descTag) {
      descTag = document.createElement('meta')
      descTag.setAttribute('name', 'description')
      document.head.appendChild(descTag)
    }
    descTag.setAttribute('content', description)

    // 3. Robots (noindex, follow) - SEO requirement
    let robotsTag = document.querySelector('meta[name="robots"]')
    if (!robotsTag) {
      robotsTag = document.createElement('meta')
      robotsTag.setAttribute('name', 'robots')
      document.head.appendChild(robotsTag)
    }
    // If noindex is explicitly specified in meta, set it. Otherwise, default to index.
    robotsTag.setAttribute('content', to.meta.noindex ? 'noindex, follow' : 'index, follow')

    // 4. Open Graph Tags
    const ogData = {
      'og:title': to.meta.ogTitle || to.meta.title || 'Satoshi Eye',
      'og:description': to.meta.ogDescription || description,
      'og:url': `https://satoshieye.com${to.fullPath}`,
      'og:type': 'website',
      'og:image': to.meta.ogImage || 'https://satoshieye.com/images/og-image.png',
      'twitter:image': to.meta.twitterImage || 'https://satoshieye.com/images/twitter-card.png',
    };
    Object.entries(ogData).forEach(([prop, val]) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', prop);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', val);
    });

    // 5. Canonical Link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    if (to.meta.dynamicCanonical) {
      canonicalTag.setAttribute('href', `https://satoshieye.com${to.path}`);
    } else if (to.meta.canonical) {
      canonicalTag.setAttribute('href', to.meta.canonical);
    } else {
      // For other pages, remove canonical or set a base one (per SEO preference)
      canonicalTag.setAttribute('href', `https://satoshieye.com${to.path}`);
    }

    // 6. JSON-LD Schema (REQ-2: only where needed)
    const ldJsonTag = document.getElementById('json-ld-block');
    if (ldJsonTag) {
      ldJsonTag.text = to.meta.jsonLD ? JSON.stringify(to.meta.jsonLD) : '';
    }

    // 7. Specific classes and promo logic
    const html = document.documentElement
    html.classList.toggle('landing-page', to.name === 'landing-page')

    const existPromo = localStorage.getItem('promocode')
    if (to.name == 'payment' && existPromo) {
      store.dispatch('adminPanel/redeemCode', existPromo)
      localStorage.removeItem('promocode')
    }
  })
})

router.onError(error => {
  console.error(error)
  if (/ChunkLoadError:.*failed./i.test(error.message) || /Loading.*chunk.*failed./i.test(error.message)) {
    window.location.reload()
  }
})

export default router
